import styled from '@emotion/styled';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@material-ui/core';
import { ClassAttributes, createElement, Fragment } from 'react';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import { StateHandler, withStateHandlers } from 'recompose';

import { UserOptimizer } from '../../../graphql/generated/graphql';
import { MatchingFormat, OfferWorkerStatus, WorkerPermission } from '../../../graphql/generated/graphql_offer';
import Spinner from '../../component/compound/Spinner';
import ButtonWithIcon from '../../component/primitive/ButtonWithIcon';
import { ArrowCircleRight } from '../../component/primitive/Icons';
import LfToBr from '../../component/primitive/LfToBr';
import RouterLink from '../../component/primitive/RouterLink';
import { SPACING_UNIT } from '../../constant/spacing';
import { Consumer as ConfigConsumer } from '../../context/config';

interface Props extends ClassAttributes<HTMLElement> {
  offer: {
    id: string;
    workerStatus: OfferWorkerStatus;
    workerPermissions: WorkerPermission[];
    matchingFormat: MatchingFormat;
  };
  mutating?: boolean;
  signedInUser: UserOptimizer;
  applyForOffer: (offerId: string) => void;
  className?: string;
}

type State = {
  isPersonalInfoDialogOpen: boolean;
  dialogInputButtonClicked: boolean;
};

type StateUpdater = {
  openPersonalInfoDialog: StateHandler<State>;
  closePersonalInfoDialog: StateHandler<State>;
  setDialogInputButtonClicked: StateHandler<State>;
};

const OfferAction = withStateHandlers<State, StateUpdater, Props>(
  {
    isPersonalInfoDialogOpen: false,
    dialogInputButtonClicked: false,
  },
  {
    openPersonalInfoDialog: () => () => ({ isPersonalInfoDialogOpen: true }),
    closePersonalInfoDialog: () => () => ({ isPersonalInfoDialogOpen: false }),
    setDialogInputButtonClicked: () => (v: boolean) => ({ dialogInputButtonClicked: v }),
  },
)(
  ({
    isPersonalInfoDialogOpen,
    dialogInputButtonClicked,
    openPersonalInfoDialog,
    closePersonalInfoDialog,
    setDialogInputButtonClicked,
    offer,
    applyForOffer,
    mutating,
    signedInUser,
    className,
  }) => {
    if (mutating) {
      return <Spinner height={24} className={className} />;
    }

    if (offer.workerStatus === 'CANCELED' || offer.workerStatus === 'REJECTED') {
      return null;
    }

    if (offer.workerStatus === 'APPLIED' && offer.matchingFormat == 'competition') {
      return (
        <RouterLink href="/projects">
          <ButtonWithIcon
            key="offer_action.APPLIED"
            className={className}
            icon={ArrowCircleRight}
            color="primary"
            variant="outlined"
          >
            <FormattedMessage id="offer_detail_page.actions.proposal" />
          </ButtonWithIcon>
        </RouterLink>
      );
    }

    if (offer.workerStatus === 'APPLIED' && offer.matchingFormat == 'auto') {
      return (
        <Label key="offer_action.APPLIED" className={className}>
          <FormattedMessage id="offer_detail_page.actions.applied">
            {(text: string) => <LfToBr text={text} />}
          </FormattedMessage>
        </Label>
      );
    }

    if (offer.workerStatus === 'ASSIGNED') {
      return (
        <RouterLink href="/projects">
          <ButtonWithIcon key="offer_action.ASSIGNED" className={className} icon={ArrowCircleRight} color="primary">
            <FormattedMessage id="offer_detail_page.actions.assigned" />
          </ButtonWithIcon>
        </RouterLink>
      );
    }

    if (offer.workerStatus === 'PRISTINE' && offer.workerPermissions.includes('APPLY_FOR_OFFER')) {
      let onClick = undefined;
      let dialog = null;

      if (!dialogInputButtonClicked && signedInUser.personalInformationStatus !== 'ACTIVE') {
        onClick = openPersonalInfoDialog;
        dialog = (
          <ConfigConsumer>
            {(config) => (
              <Dialog
                open={isPersonalInfoDialogOpen}
                aria-labelledby="persona-info-dialog-title"
                aria-describedby="persona-info-dialog-message"
                onClose={closePersonalInfoDialog}
              >
                <DialogTitle id="persona-info-dialog-title">
                  {signedInUser.personalInformationStatus === 'BLOCKED' ? (
                    <FormattedMessage id="offer_detail_page.personal_info_dialog.blocked_title" />
                  ) : (
                    <FormattedMessage id="offer_detail_page.personal_info_dialog.title" />
                  )}
                </DialogTitle>
                <DialogContent>
                  <DialogContentText id="alert-dialog-slide-message">
                    {signedInUser.personalInformationStatus === 'BLOCKED' ? (
                      <FormattedHTMLMessage id="offer_detail_page.personal_info_dialog.blocked_message" />
                    ) : (
                      <FormattedMessage id="offer_detail_page.personal_info_dialog.message" />
                    )}
                  </DialogContentText>
                </DialogContent>
                {signedInUser.personalInformationStatus !== 'BLOCKED' && (
                  <DialogActions>
                    <Button
                      onClick={() => {
                        window.open(`${config.ID_WEB_URL}/user_setting/personal`, '_blank');
                        setDialogInputButtonClicked(true);
                        closePersonalInfoDialog();
                      }}
                      color="primary"
                    >
                      <FormattedMessage id="offer_detail_page.personal_info_dialog.button_label" />
                    </Button>
                  </DialogActions>
                )}
              </Dialog>
            )}
          </ConfigConsumer>
        );
      } else if (dialogInputButtonClicked || offer.workerPermissions.includes('APPLY_FOR_OFFER')) {
        onClick = () => applyForOffer(offer.id);
      }

      return (
        <div className={className}>
          {offer.matchingFormat == 'competition' ? (
            <Fragment>
              <OfferActionButton
                key="offer_action.APPLY_FOR_OFFER"
                variant="contained"
                color="primary"
                onClick={onClick}
              >
                <FormattedMessage id="offer_detail_page.actions.propose_to_offer" />
              </OfferActionButton>
            </Fragment>
          ) : (
            <OfferActionButton key="offer_action.APPLY_FOR_OFFER" variant="contained" color="primary" onClick={onClick}>
              <FormattedMessage id="offer_detail_page.actions.apply_for_offer" />
            </OfferActionButton>
          )}
          {dialog}
        </div>
      );
    }

    return null;
  },
);

const Label = styled(Typography)({});
Label.defaultProps = { color: 'primary', variant: 'button' };

const ActionButtonHelperText = styled(Typography)({
  marginBottom: SPACING_UNIT,
});
ActionButtonHelperText.defaultProps = { variant: 'body1' };

const OfferActionButton = styled(Button)({
  marginRight: SPACING_UNIT,
});

export default OfferAction;
